import { useState, useEffect } from 'react';
import {
  getTenantMatchingData,
  linkClientToTenant,
  PartnerCenterCustomer,
  UnmatchedClient
} from '../../services/api';
import { Link, CheckCircle } from 'lucide-react';

interface TenantMatcherProps {
  onRefresh?: () => void;
}

export default function TenantMatcher({ onRefresh }: TenantMatcherProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [partnerCenterCustomers, setPartnerCenterCustomers] = useState<PartnerCenterCustomer[]>([]);
  const [unmatchedClients, setUnmatchedClients] = useState<UnmatchedClient[]>([]);
  const [selectedLinks, setSelectedLinks] = useState<Record<string, string>>({});
  const [linkingId, setLinkingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTenantMatchingData();
      setPartnerCenterCustomers(data.partnerCenterCustomers);
      setUnmatchedClients(data.unmatchedClients);
    } catch (err) {
      setError('Failed to load tenant matching data. Run "Sync Tenant IDs" first to populate Partner Center customers.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSelectTenant = (clientId: string, tenantId: string) => {
    setSelectedLinks(prev => ({
      ...prev,
      [clientId]: tenantId
    }));
  };

  const handleLink = async (clientId: string) => {
    const tenantId = selectedLinks[clientId];
    if (!tenantId) return;

    setLinkingId(clientId);
    try {
      const result = await linkClientToTenant(clientId, tenantId);
      if (result.success) {
        // Remove from unmatched list
        setUnmatchedClients(prev => prev.filter(c => c.id !== clientId));
        setSelectedLinks(prev => {
          const newLinks = { ...prev };
          delete newLinks[clientId];
          return newLinks;
        });
        setSuccessMessage(`Linked ${result.client.companyName} successfully!`);
        setTimeout(() => setSuccessMessage(null), 3000);
        onRefresh?.();
      }
    } catch (err) {
      setError('Failed to link client');
    } finally {
      setLinkingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-muted-foreground">Loading matching data...</span>
      </div>
    );
  }

  if (error && partnerCenterCustomers.length === 0) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <p className="text-yellow-800 dark:text-yellow-200">{error}</p>
      </div>
    );
  }

  if (unmatchedClients.length === 0) {
    return (
      <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 flex items-center gap-2">
        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
        <p className="text-green-800 dark:text-green-200">All clients have tenant IDs assigned!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          <span className="text-green-800 dark:text-green-200">{successMessage}</span>
        </div>
      )}

      <div className="text-sm text-muted-foreground mb-2">
        <strong>{unmatchedClients.length}</strong> Pax8 clients without tenant IDs.
        Select a Partner Center customer to link each one.
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Pax8 Client
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Partner Center Customer
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="bg-background divide-y divide-border">
            {unmatchedClients.map(client => (
              <tr key={client.id} className="hover:bg-accent">
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="text-sm font-medium text-foreground">
                    {client.companyName}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <select
                    value={selectedLinks[client.id] || ''}
                    onChange={(e) => handleSelectTenant(client.id, e.target.value)}
                    className="block w-full rounded-md border-border bg-card text-foreground shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                  >
                    <option value="">-- Select Partner Center Customer --</option>
                    {partnerCenterCustomers.map(pc => (
                      <option key={pc.tenantId} value={pc.tenantId}>
                        {pc.companyName} ({pc.domain})
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <button
                    onClick={() => handleLink(client.id)}
                    disabled={!selectedLinks[client.id] || linkingId === client.id}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {linkingId === client.id ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                        Linking...
                      </>
                    ) : (
                      <>
                        <Link className="h-3 w-3 mr-1" />
                        Link
                      </>
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {partnerCenterCustomers.length > 0 && (
        <div className="text-xs text-muted-foreground mt-2">
          {partnerCenterCustomers.length} Partner Center customers available for linking.
          Last synced: {partnerCenterCustomers[0]?.lastSyncedAt ? new Date(partnerCenterCustomers[0].lastSyncedAt).toLocaleString() : 'Unknown'}
        </div>
      )}
    </div>
  );
}
